/**
 * React hooks for memoization and local state management
 */
import { useCallback, useState } from "react";

/**
 * Toast utilities for showing success/error notifications
 */
import { toast, useToast } from "./use-toast";

/**
 * Next.js navigation hooks
 * - useParams: access dynamic route params
 * - useRouter: client-side navigation
 * - useSearchParams: access query string params
 */
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { apiFetch } from "@/lib/api/apiFetch";

/**
 * Campaign model representing a single email campaign
 * Used mainly in campaign listing and high-level operations
 */
export interface Campaign {
  /** Unique campaign identifier */
  _id: string

  /** Internal campaign name */
  name: string

  /** Display-friendly campaign name */
  displayName: string

  /** Email subject line */
  subject: string

  /** Current campaign lifecycle status */
  status: "Draft" | "Scheduled" | "Sending" | "Sent" | "Paused" | "Cancelled" | "Failed"

  /** Selected email template information */
  template: {
    _id: string
    name: string
    subject: string
  }

  /** Recipient lists attached to this campaign */
  recipientLists: Array<{
    _id: string
    name: string
    contactCount: number
    emailStats: {
      safe: number
      invalid: number,
      risky: number,
      unverified: number,
      bounced: number
    }

  }>

  /** Optional internal remarks or notes */
  remarks?: string

  /** Aggregated campaign statistics */
  statistics: {
    totalRecipients: number
    emailsSent: number
    emailsDelivered: number
    emailsBounced: number
    emailsOpened: number
    emailsClicked: number
    unsubscribes: number
    complaints: number
    openRate: number
    clickRate: number
    bounceRate: number
    unsubscribeRate: number
  }

  /** Campaign-level delivery and tracking settings */
  settings: {
    enableIPWarmup: boolean
    enableUnsubscribeLink: boolean
    threads: "auto" | "custom"
    customThreads?: number
    trackOpens: boolean
    trackClicks: boolean
    enableRetry: boolean
    retryAttempts: number
  }

  /** Scheduled send time (ISO string) */
  scheduledAt?: string

  /** Actual send start time */
  sentAt?: string

  /** Campaign completion timestamp */
  completedAt?: string

  /** Creation timestamp */
  createdAt: string

  /** Last update timestamp */
  updatedAt: string

  /** User/system who created the campaign */
  createdBy: string
  /** Test email addresses associated with the campaign */
  testEmails: string[]
  /** Email configuration */
  emailConfig: {
    fromName: string
    fromEmail: string
    // bcc: string[]
    replyTo: string
    confirmationEmail?: string
  }
}

/**
 * API response structure for campaign list endpoint
 */
interface ApiResponse {
  /** Array of campaign records */
  data: Campaign[]

  /** Pagination metadata */
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

/**
 * Email template model used when creating or editing campaigns
 */
interface Template {
  _id: string
  name: string
  subject: string
  content: string
  type: string
  category: string
}

/**
 * Recipient list model representing a group of contacts
 */
interface RecipientList {
  _id: string
  name: string
  description: string
  contactCount: number
}

/**
 * Campaign form payload used for create & update operations
 */
interface CampaignFormData {
  /** Campaign name */
  name: string

  /** Display name shown to recipients */
  displayName: string

  /** Email subject */
  subject: string

  /** Selected template ID */
  template: string

  /** Selected recipient list IDs */
  recipientLists: string[]

  /** Optional scheduled send time */
  scheduledAt?: string

  /** Campaign behavior and tracking settings */
  settings: {
    enableIPWarmup: boolean
    enableUnsubscribeLink: boolean
    threads: "auto" | "custom"
    customThreads?: number
    trackOpens: boolean
    trackClicks: boolean
    enableRetry: boolean
    retryAttempts: number
  }

  /** Test email addresses */
  testEmails: string[]

  /** Internal remarks */
  remarks: string
  /** Email configuration */
  emailConfig?: {
    fromName: string
    fromEmail: string
    // bcc: string[]
    replyTo: string
    confirmationEmail: string
  }
}

/**
 * Detailed campaign model used in campaign details page
 */
interface CampaignDetails {
  _id: string
  name: string
  displayName: string
  subject: string
  status: any
  scheduledAt?: string
  sentAt?: string
  completedAt?: string

  /** Tracking configuration */
  settings: {
    trackOpens: boolean
    trackClicks: boolean
    enableUnsubscribeLink: boolean
  }

  /** Performance statistics */
  statistics: {
    totalRecipients: number
    emailsSent: number
    emailsDelivered: number
    emailsBounced: number
    emailsOpened: number
    emailsClicked: number
    unsubscribes: number
    complaints: number
    openRate: number
    clickRate: number
    bounceRate: number
    unsubscribeRate: number
  }

  /** Template used by the campaign */
  template: {
    _id: string
    name: string
    type: string
  }

  /** Recipient lists with contact counts */
  recipientLists: Array<{
    _id: string
    name: string
    contactCount: number
  }>

  /** Test email addresses */
  testEmails: string[]

  /** Optional remarks */
  remarks?: string

  /** Campaign tags */
  tags: string[]

  /** Creator of the campaign */
  createdBy: string

  /** Timestamps */
  createdAt: string
  updatedAt: string

  /** Optional populated contacts list */
  contacts?: Contact[]
}

/**
 * Individual contact associated with a campaign
 */
interface Contact {
  _id: string
  name: string
  email: string
  status: any
  source: string
  createdAt: string
}

/**
 * Extra metadata related to delivery or click events
 */
interface eventData {
  deliveryStatus?: "delivered" | "failed" | "pending"
  deliveryMessage?: string
  clickCount?: number
  deliveryTimestamp?: string
}

/**
 * Email tracking log entry
 * Represents a single delivery, open, click, bounce, or spam event
 */
interface EmailTracking {
  _id: string
  campaignId: string
  emailId: string
  recipientEmail: string
  eventType: "delivery" | "bounce" | "spam" | "open" | "click" | "unsubscribe"
  timestamp: string
  userAgent?: string
  ipAddress?: string
  linkUrl?: string
  bounceReason?: string
  spamReason?: string
  eventData?: eventData
}


/**
 * Custom React hook that encapsulates all campaign-related logic.
 *
 * Responsibilities:
 * - Campaign listing with pagination & search
 * - Campaign create/edit form state
 * - Campaign send, archive, duplicate actions
 * - Campaign details and email tracking
 *
 * @returns An object containing campaign state, handlers, and helpers
 */
export const useCampaignHook = () => {

  /**
   * Pagination state for campaign list & tracking list
   * - page: current page number
   * - limit: records per page
   * - total: total record count
   * - pages: total pages available
   */
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  })

  /**
   * Internal loading flags for different data types
   */
  const [isDataLoading, setIsDataLoading] = useState(true)
  const [isCampaignLoading, setIsCampaignLoading] = useState(false)
  const [isGlobalLoading, setIsGlobalLoading] = useState(false)

  /**
   * Global loading state for campaign-related API calls
   * Derived from individual loading flags to prevent race conditions
   */
  const isLoading = isDataLoading || isCampaignLoading || isGlobalLoading

  /**
   * Loading state specifically for analytics data
   */
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false)
  const [logsLoading, setLogsLoading] = useState(false)

  /**
   * Search term used to filter campaign list
   */
  const [searchTerm, setSearchTerm] = useState("")

  /**
   * List of campaigns fetched from API
   */
  const [campaigns, setCampaigns] = useState<Campaign[]>([])

  /**
   * Campaign currently selected for sending
   */
  const [campaignToSend, setCampaignToSend] = useState<Campaign | null>(null)

  /**
   * Controls visibility of "Send Campaign" confirmation dialog
   */
  const [showSendDialog, setShowSendDialog] = useState(false)

  /**
   * Campaign currently selected for archive action
   */
  const [campaignToArchive, setCampaignToArchive] = useState<Campaign | null>(null)

  /**
   * Set of campaign IDs that are currently in sending state
   * Used to disable buttons and show loaders per campaign
   */
  const [sendingCampaigns, setSendingCampaigns] = useState<Set<string>>(new Set())

  /**
   * Controls visibility of "Archive Campaign" confirmation dialog
   */
  const [showArchiveDialog, setShowArchiveDialog] = useState(false)

  /**
   * Toast utility for user feedback (success / error messages)
   */
  const { toast } = useToast()

  /**
   * Next.js router instance for client-side navigation
   */
  const router = useRouter()

  /**
   * Route params from dynamic route segments
   */
  const params = useParams()

  //-------------------------------------------------

  /**
   * Query parameters from URL (used mainly for edit mode)
   */
  const searchParams: any = useSearchParams();

  /**
   * Campaign ID from query string (?id=...)
   * Used to determine edit vs create mode
   */
  const campaignId = searchParams.get('id');

  /**
   * Campaign data loaded for edit mode
   */
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);

  /**
   * Available email templates
   */
  const [templates, setTemplates] = useState<Template[]>([])

  /**
   * Available recipient lists
   */
  const [recipientLists, setRecipientLists] = useState<RecipientList[]>([])

  /**
   * Form state for create / update campaign
   */
  const [formData, setFormData] = useState<CampaignFormData>({
    name: "",
    displayName: "",
    subject: "",
    template: "",
    recipientLists: [],
    settings: {
      enableIPWarmup: true,
      enableUnsubscribeLink: true,
      threads: "auto",
      trackOpens: true,
      trackClicks: true,
      enableRetry: true,
      retryAttempts: 3,
    },
    testEmails: [],
    remarks: "",
    emailConfig: {
      fromName: "",
      fromEmail: "",
      // bcc: [],
      replyTo: "",
      confirmationEmail: "",
    },
  })

  /**
   * Currently selected template object (derived from template ID)
   */
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null)

  /**
   * Selected recipient list objects (derived from selected IDs)
   */
  const [selectedLists, setSelectedLists] = useState<RecipientList[]>([])

  /**
   * Indicates whether campaign create/update request is in progress
   */
  const [isSubmitting, setIsSubmitting] = useState(false)

  /**
   * Indicates whether default template creation is in progress
   */
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false)

  /**
   * Temporary input value for adding test email address
   */
  const [testEmail, setTestEmail] = useState("")

  //-------------------------------------------

  /**
   * Campaign details data for campaign details page
   */
  const [campaign, setCampaign] = useState<CampaignDetails | null>(null)

  /**
   * Contacts associated with a campaign (populated on details page)
   */
  const [contacts, setContacts] = useState<Contact[]>([])

  /**
   * Email tracking logs for a campaign
   */
  const [trackingLogs, setTrackingLogs] = useState<EmailTracking[]>([])

  /**
   * Active tab on campaign details page (details | contacts | tracking)
   */
  const [activeTab, setActiveTab] = useState("details")

  /***
   * campaing analytics details data
   */

  const [campaignAnalytics, setCampaignAnalytics] = useState<any | null>(null)

  /**
   * Pagination state for tracking logs table
   */
  const [trackingPagination, setTrackingPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  })

  const [campaignLogs, setCampaignLogs] = useState<any | []>([])


  /**
 * Fetches campaign list from API with pagination and search support.
 *
 * Side effects:
 * - Updates `campaigns` state
 * - Updates `pagination.total` and `pagination.pages`
 * - Toggles `isLoading`
 *
 * @returns Promise<void>
 */
  const fetchCampaigns = useCallback(async () => {
    try {
      setIsGlobalLoading(true)
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...(searchTerm && { search: searchTerm }),
      })

      const data = await apiFetch(`/api/campaigns?${params}`)
      setCampaigns(data.data)

      setPagination(prev => ({
        ...prev,
        total: data.pagination.total, // ✅ use pagination from API
        pages: data.pagination.pages
      }))
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load campaigns",
        variant: "destructive",
      })
    } finally {
      setIsGlobalLoading(false)
    }
  }, [pagination.page, pagination.limit, searchTerm])

  /**
   * Opens send confirmation dialog for the selected campaign.
   *
   * @param campaignId ID of the campaign to be sent
   * @returns Promise<void>
   */
  const handleSendCampaign = async (campaignId: string) => {
    const campaign = campaigns.find(c => c._id === campaignId)
    if (!campaign) return

    setCampaignToSend(campaign)
    setShowSendDialog(true)
  }

  /**
   * Navigates user to campaign edit page.
   *
   * @param campaign Campaign object to edit
   * @returns void
   */
  const handleEditCampaign = (campaign: Campaign) => {
    const params = new URLSearchParams({
      // mode: 'edit',
      id: campaign._id,
    });
    router.push(`/campaigns/new?${params.toString()}`);
  }

  const handleDetailsCampaign = (campaign: Campaign) => {
    const params = new URLSearchParams({
      // mode: 'view',
      id: campaign._id,
    });
    router.push(`/campaigns/details?${params.toString()}`);
  }

  /**
   * Sends the selected campaign after confirmation.
   *
   * Side effects:
   * - Updates `sendingCampaigns`
   * - Calls send API
   * - Refreshes campaign list
   * - Shows success/error toast
   *
   * @returns Promise<void>
   */
  const confirmSendCampaign = async () => {
    if (!campaignToSend) return

    try {
      // Add campaign to sending state
      setSendingCampaigns(prev => new Set(prev).add(campaignToSend._id))

      const data = await apiFetch(`/api/campaigns/${campaignToSend._id}/send`, {
        method: "POST",
        timeoutMs: 180000,
      })

      toast({
        title: "Campaign Sending Started",
        description: `Campaign "${campaignToSend.name}" is now sending to ${data?.totalRecipients} recipients. You'll receive updates as it progresses.`,
      })

      // Refresh campaigns to update status
      await fetchCampaigns()
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send campaign",
        variant: "destructive",
      })
    } finally {
      // Remove campaign from sending state
      setSendingCampaigns(prev => {
        const newSet = new Set(prev)
        newSet.delete(campaignToSend._id)
        return newSet
      })
      setShowSendDialog(false)
      setCampaignToSend(null)
    }
  }

  /**
   * Opens archive confirmation dialog for a campaign.
   *
   * @param campaignId ID of the campaign to archive
   * @returns Promise<void>
   */
  const handleDeleteCampaign = async (campaignId: string) => {
    const campaign = campaigns.find(c => c._id === campaignId)
    if (!campaign) return

    setCampaignToArchive(campaign)
    setShowArchiveDialog(true)
  }

  /**
   * Confirms campaign archive action.
   *
   * @returns Promise<void>
   */
  const confirmArchiveCampaign = async () => {
    if (!campaignToArchive) return

    await handleCampaignAction(campaignToArchive._id, "archive", () => {
      setShowArchiveDialog(false)
      setCampaignToArchive(null)
    })
  }

  /**
   * Duplicates an existing campaign.
   *
   * @param campaignId ID of the campaign to duplicate
   * @returns Promise<void>
   */
  const handleDuplicateCampaign = async (campaignId: string) => {
    if (!campaigns) return
    const campaign = campaigns.find(c => c._id === campaignId)
    if (!campaign) return
    await handleCampaignAction(campaign._id, "duplicate")
  }

  /**
   * Handles campaign management actions like archive or duplicate.
   *
   * @param campaignId Campaign ID
   * @param action Action type ("archive" | "duplicate")
   * @param onSuccess Optional callback executed after success
   *
   * @returns Promise<void>
   */
  const handleCampaignAction = async (
    campaignId: string,
    action: "archive" | "duplicate",
    onSuccess?: (data?: any) => void
  ) => {
    try {
      const result = await apiFetch(`/api/campaigns/${campaignId}/manage`, {
        method: "PUT",
        data: { action },
      }).catch(() => ({})) // archive may not return JSON

      toast({
        title: "Success",
        description:
          action === "archive"
            ? "Campaign archived successfully"
            : `Campaign duplicated successfully: ${result?.data?.duplicatedCampaignName ?? ""}`,
      })

      await fetchCampaigns()
      onSuccess?.(result)
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || `Failed to ${action} campaign`,
        variant: "destructive",
      })
    }
  }

  /**
   * Returns Tailwind CSS classes based on campaign status.
   *
   * @param status Campaign status
   * @returns CSS class string
   */
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Sent":
        return "bg-green-100 text-green-800"
      case "Sending":
        return "bg-blue-100 text-blue-800"
      case "Scheduled":
        return "bg-yellow-100 text-yellow-800"
      case "Draft":
        return "bg-gray-100 text-gray-800"
      case "Paused":
        return "bg-orange-100 text-orange-800"
      case "Cancelled":
        return "bg-red-100 text-red-800"
      case "Failed":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  /**
   * Calculates aggregated statistics across all campaigns.
   *
   * @returns Object containing total sent, delivered, opened,
   * clicked, and bounced counts.
   */
  const calculateTotalStats = () => {
    if (!campaigns || !Array.isArray(campaigns) || campaigns.length === 0) {
      return {
        totalSent: 0,
        totalDelivered: 0,
        totalOpened: 0,
        totalClicked: 0,
        totalBounced: 0,
      }
    }

    return campaigns.reduce(
      (acc, campaign) => {
        if (campaign.statistics) {
          acc.totalSent += campaign.statistics.emailsSent || 0
          acc.totalDelivered += campaign.statistics.emailsDelivered || 0
          acc.totalOpened += campaign.statistics.emailsOpened || 0
          acc.totalClicked += campaign.statistics.emailsClicked || 0
          acc.totalBounced += campaign.statistics.emailsBounced || 0
        }
        return acc
      },
      {
        totalSent: 0,
        totalDelivered: 0,
        totalOpened: 0,
        totalClicked: 0,
        totalBounced: 0,
      }
    )
  }



  // new page function  ------------------------------------------

  /**
   * Fetches campaign details for edit mode.
   *
   * Uses `campaignId` from query params to load
   * an existing campaign and populate edit state.
   *
   * Side effects:
   * - Updates `editingCampaign`
   * - Toggles `isLoading`
   *
   * @returns Promise<void>
   */
  const fetchCampaignDetails = async () => {
    try {
      setIsCampaignLoading(true)

      // Fetch campaign details with populated data
      const campaignData = await apiFetch(`/api/campaigns/${campaignId}`)
      setEditingCampaign(campaignData?.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch campaign details",
        variant: "destructive",
      })
    } finally {
      setIsCampaignLoading(false)
    }
  }

  const fetchCampaignLogs = async (id?: string) => {
    try {
      setLogsLoading(true)
      const targetId = id || campaignId;
      if (!targetId) return;

      const logs = await apiFetch(`/api/campaigns/${targetId}/logs`)
      setCampaignLogs(logs.data)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch campaign logs",
        variant: "destructive",
      })
    } finally {
      setLogsLoading(false)
    }
  }

  /**
   * Fetches supporting data required for campaign creation/editing.
   *
   * Loads:
   * - Email templates
   * - Recipient lists
   *
   * Side effects:
   * - Updates `templates`
   * - Updates `recipientLists`
   * - Toggles `isLoading`
   *
   * @returns Promise<void>
   */
  const fetchData = async () => {
    try {
      setIsDataLoading(true)
      const [templatesData, listsData] = await Promise.all([
        apiFetch("/api/templates"),
        apiFetch("/api/recipient-lists?limit=20"),
      ])

      setTemplates(templatesData.templates || [])
      setRecipientLists(listsData.lists || [])
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load campaign data",
        variant: "destructive",
      })
    } finally {
      setIsDataLoading(false)
    }
  }

  /**
   * Handles template selection change.
   *
   * Updates:
   * - Selected template object
   * - Campaign form template ID
   *
   * @param templateId Selected template ID
   * @returns void
   */
  const handleTemplateChange = async (templateId: string) => {
    if (!templateId) {
      setSelectedTemplate(null)
      return
    }
    const template = await apiFetch(`/api/templates/${templateId}`)
    setSelectedTemplate(template || null)
    setFormData((prev) => ({
      ...prev,
      template: templateId,
      // subject: template?.subject || prev.subject,
    }))
  }

  // const handleTemplateChange = (templateId: string) => {
  //   const template = templates.find((t) => t._id === templateId)
  //   setSelectedTemplate(template || null)
  //   setFormData((prev) => ({
  //     ...prev,
  //     template: templateId,
  //     // subject: template?.subject || "",
  //   }))
  // }

  /**
   * Toggles recipient list selection.
   *
   * Updates:
   * - `formData.recipientLists`
   * - `selectedLists` (for UI display)
   *
   * @param listId Recipient list ID
   * @returns void
   */
  const handleListToggle = (listId: string) => {
    const isSelected = formData.recipientLists.includes(listId)
    const updatedLists = isSelected
      ? formData.recipientLists.filter((id) => id !== listId)
      : [...formData.recipientLists, listId]

    setFormData({ ...formData, recipientLists: updatedLists })

    // Update selected lists for display
    const lists = recipientLists.filter((list) => updatedLists.includes(list._id))
    setSelectedLists(lists)
  }

  const handleListsFetched = (newLists: any[]) => {
      setRecipientLists((prev: any[]) => {
          const map = new Map(prev.map(l => [l._id, l]));
          newLists.forEach(l => map.set(l._id, l));
          return Array.from(map.values());
      });
  }

  /**
   * Creates a default professional email template.
   *
   * Side effects:
   * - Creates template via API
   * - Refreshes templates list
   * - Auto-selects newly created template
   * - Shows success/error toast
   *
   * @returns Promise<void>
   */
  const createDefaultTemplate = async () => {
    try {
      setIsCreatingTemplate(true)
      const newTemplate = await apiFetch("/api/templates", {
        method: "POST",
        data: {
          name: "Professional Welcome Email",
          subject: "Welcome to iComm - Your Communication Platform",
          category: "Welcome",
          createdBy: "system",
        },
      })

      // Refresh templates list
      await fetchData()

      // Select the new template
      handleTemplateChange(newTemplate._id)

      toast({
        title: "Success",
        description: "Professional email template created successfully",
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create template",
        variant: "destructive",
      })
    } finally {
      setIsCreatingTemplate(false)
    }
  }

  /**
   * Handles campaign create or update form submission.
   *
   * Behavior:
   * - Validates required fields
   * - Determines create vs update using `campaignId`
   * - Calls appropriate API
   * - Redirects to campaign list on success
   *
   * @param e React form submit event
   * @returns Promise<void>
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Campaign name is required",
        variant: "destructive",
      })
      return
    }

    if (!formData.subject.trim()) {
      toast({
        title: "Validation Error",
        description: "Email subject is required",
        variant: "destructive",
      })
      return
    }

    if (!formData.template) {
      toast({
        title: "Validation Error",
        description: "Please select a template",
        variant: "destructive",
      })
      return
    }

    if (formData.recipientLists.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one recipient list",
        variant: "destructive",
      })
      return
    }
    setIsSubmitting(true)

    try {
      const url = campaignId
        ? `/api/campaigns/${campaignId}`
        : '/api/campaigns';

      const method = campaignId ? 'PUT' : 'POST';

      const newCampaign = await apiFetch(url, {
        method,
        data: {
          ...formData,
          name: formData.name.trim(),
          displayName: formData.displayName.trim() || formData.name.trim(),
          subject: formData.subject.trim(),
          remarks: formData.remarks.trim(),
          createdBy: "system",
        },
      })

      toast({
        title: "Success",
        description: `Campaign "${formData.name}" ${campaignId ? 'updated' : 'created'} successfully`,
      })

      router.push("/campaigns")
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create campaign",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * Adds a test email address to the test email list.
   *
   * Prevents duplicates and clears input field after adding.
   *
   * @returns void
   */
  const addTestEmail = () => {
    if (testEmail && !formData.testEmails.includes(testEmail)) {
      setFormData({
        ...formData,
        testEmails: [...formData.testEmails, testEmail],
      })
      setTestEmail("")
    }
  }

  /**
   * Sends a test email using selected template and subject.
   *
   * Validates required fields before sending.
   *
   * @returns Promise<void>
   */
  const sendTestEmail = async (overrideData?: {
    testEmail?: string
    template?: string
    subject?: string
    displayName?: string
  }) => {
    try {
      const emailToSend = overrideData?.testEmail ?? testEmail
      const templateToSend = overrideData?.template ?? formData.template
      const subjectToSend = overrideData?.subject ?? formData.subject
      const displayNameToSend = overrideData?.displayName ?? formData.displayName

      if (!emailToSend || !templateToSend || !subjectToSend || !displayNameToSend) {
        toast({
          title: "Error",
          description: "Please fill in all required fields before sending a test email.",
          variant: "destructive",
        })
        return
      }
      await apiFetch('/api/campaigns/test-email', {
        method: 'POST',
        data: {
          testEmail: emailToSend,
          template: templateToSend,
          subject: subjectToSend,
          displayName: displayNameToSend,
        },
        timeoutMs: 180000,
      })

      toast({
        title: "Success",
        description: "Test email sent successfully",
      })
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send test email",
        variant: "destructive",
      })
    }
  }

  /**
   * Removes a test email address from the list.
   *
   * @param email Email address to remove
   * @returns void
   */
  const removeTestEmail = (email: string) => {
    setFormData({
      ...formData,
      testEmails: formData.testEmails.filter((e) => e !== email),
    })
  }


  // campaign details ---------------------------------------------

  /**
   * Fetches campaign details for the campaign details page.
   *
   * Uses route param (`params.id`) to load:
   * - Campaign core details
   * - Associated contacts
   * - Initial tracking logs
   *
   * Side effects:
   * - Updates `campaign`
   * - Updates `contacts`
   * - Triggers tracking logs fetch
   * - Toggles `isLoading`
   *
   * @returns Promise<void>
   */
  const fetchCampaignDetailsPage = async (id: string) => {
    try {
      setIsCampaignLoading(true)

      // console.log(`/api/campaigns/${id}`, "campaignResponse")
      // Fetch campaign details with populated data
      const campaignData = await apiFetch(`/api/campaigns/${id}`)

      setCampaign(campaignData?.data);
      setContacts(campaignData?.data?.contacts || [])

      handleTemplateChange(campaignData?.data?.template?._id)
      // Fetch tracking logs separately
      await fetchTrackingLogs(campaignData?.data._id)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch campaign details",
        variant: "destructive",
      })
    } finally {
      setIsCampaignLoading(false)
    }
  }


  const fetchCampaignDetailsAnalytics = async (id: string, range: string = "today", startDate?: string, endDate?: string) => {
    try {
      setIsAnalyticsLoading(true)
      const params = new URLSearchParams({ range })
      if (range === 'custom' && startDate) params.append('startDate', startDate)
      if (range === 'custom' && endDate) params.append('endDate', endDate)

      const data = await apiFetch(`/api/campaigns/${id}/analytics?${params.toString()}`)
      setCampaignAnalytics(data.data)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch campaign analytics",
        variant: "destructive",
      })
    } finally {
      setIsAnalyticsLoading(false)
    }
  }

  /**
   * Fetches email tracking logs for a campaign.
   *
   * Supports pagination for large tracking datasets.
   *
   * @param campaignId Campaign ID whose logs are to be fetched
   * @param page Page number (default: 1)
   * @param limit Number of records per page (default: 20)
   *
   * Side effects:
   * - Updates `trackingLogs`
   * - Updates `trackingPagination`
   *
   * @returns Promise<void>
   */
  const fetchTrackingLogs = async (
    campaignId: string,
    page: number = 1,
    limit: number = 20
  ) => {
    try {
      const data = await apiFetch(
        `/api/campaigns/${campaignId}/tracking?page=${page}&limit=${limit}`
      )

      setTrackingLogs(data.trackingLogs || [])
      setTrackingPagination({
        page: data.pagination.page,
        limit: data.pagination.limit,
        total: data.pagination.total,
        pages: data.pagination.pages
      })
    } catch (error) {
      console.error(" Error fetching tracking logs:", error)
    }
  }

  /**
   * Handles page change for tracking logs table.
   *
   * Updates current page and triggers tracking logs refetch.
   *
   * @param page New page number
   * @returns void
   */
  const handleTrackingPageChange = (page: number) => {
    if (campaign) {
      setTrackingPagination(prev => ({ ...prev, page }))
      fetchTrackingLogs(campaign._id, page, trackingPagination.limit)
    }
  }

  /**
   * Handles page size (limit) change for tracking logs table.
   *
   * Resets page to 1 and refetches tracking logs.
   *
   * @param limit New number of records per page
   * @returns void
   */
  const handleTrackingPageSizeChange = (limit: number) => {
    if (campaign) {
      setTrackingPagination(prev => ({ ...prev, page: 1, limit }))
      fetchTrackingLogs(campaign._id, 1, limit)
    }
  }

  /**
   * Returns Tailwind CSS classes based on tracking event type.
   *
   * @param eventType Tracking event type
   * @returns CSS class string
   */
  const getEventTypeColor = (eventType: string) => {
    switch (eventType) {
      case "delivery":
        return "bg-green-100 text-green-800"
      case "open":
        return "bg-blue-100 text-blue-800"
      case "click":
        return "bg-purple-100 text-purple-800"
      case "bounce":
        return "bg-red-100 text-red-800"
      case "spam":
        return "bg-orange-100 text-orange-800"
      case "unsubscribe":
        return "bg-gray-100 text-gray-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  /**
   * Determines overall delivery status for an email based on tracking logs.
   *
   * Logic priority:
   * 1. Bounce
   * 2. Spam
   * 3. Delivered (delivery/open/click)
   * 4. Failed delivery
   *
   * @param log Tracking log entry (currently used as context)
   * @returns Object containing status label and color class
   */
  const getDeliveryStatus = (log: EmailTracking) => {
    // Check if there's a delivery event for this email
    const hasDelivery = trackingLogs.some(t =>
    (
      (t.eventType === "delivery" && t?.eventData?.deliveryStatus === "delivered") ||
      t.eventType === "open" ||
      t.eventType === "click"
    )
    )

    // Check if there's a bounce event for this email
    const hasBounce = trackingLogs.some(t =>
      t.eventType === "bounce"
    )

    // Check if there's a spam event for this email
    const hasSpam = trackingLogs.some(t =>
      t.eventType === "spam"
    )

    // Check if delivery failed
    const hasFailedDelivery = trackingLogs.some(t =>
      t.eventType === "delivery" && t?.eventData?.deliveryStatus === "failed"
    )

    if (hasBounce) return { status: "Bounced", color: "bg-red-100 text-red-800" }
    if (hasSpam) return { status: "Spam", color: "bg-orange-100 text-orange-800" }
    if (hasDelivery) return { status: "Delivered", color: "bg-green-100 text-green-800" }
    if (hasFailedDelivery) return { status: "Failed", color: "bg-red-100 text-red-800" }

    return { status: "Unknown", color: "bg-gray-100 text-gray-800" }
  }


  return {

    // campaigns----------------------------

    fetchCampaigns,
    campaigns,
    handleSendCampaign,
    isLoading,
    pagination,
    setPagination,
    showSendDialog,
    setShowSendDialog,
    campaignToSend,
    handleDetailsCampaign,
    handleEditCampaign,
    handleDeleteCampaign,
    confirmSendCampaign,
    sendingCampaigns,
    campaignToArchive,
    showArchiveDialog,
    setShowArchiveDialog,
    calculateTotalStats,
    confirmArchiveCampaign,
    handleDuplicateCampaign,
    getStatusColor,
    searchTerm,

    // new page------------------------------

    fetchCampaignDetails,
    editingCampaign,
    campaignId,
    fetchData,
    templates,
    recipientLists,
    formData,
    setFormData,
    handleTemplateChange,
    handleListToggle,
    handleListsFetched,
    createDefaultTemplate,
    handleSubmit,
    addTestEmail,
    sendTestEmail,
    removeTestEmail,
    selectedLists,
    setTestEmail,
    testEmail,
    isSubmitting,
    selectedTemplate,
    setSelectedTemplate,
    setSelectedLists,

    // campaign details------------------------

    fetchCampaignDetailsPage,
    fetchTrackingLogs,
    fetchCampaignDetailsAnalytics,
    handleTrackingPageChange,
    handleTrackingPageSizeChange,
    getEventTypeColor,
    getDeliveryStatus,
    campaign,
    activeTab,
    setActiveTab,
    contacts,
    trackingPagination,
    trackingLogs,
    campaignAnalytics,
    isAnalyticsLoading,
    setSearchTerm,

    campaignLogs,
    fetchCampaignLogs,
    logsLoading,

    isGlobalLoading
  };
};
